import { z } from 'zod';
import { ChargeMode, HeatPumpMode } from './modes.ts';
import { Role } from './roles.ts';
import { SiteProfile } from './site-profile.ts';

// API contract between UI and gateway (locally) or cloud relay (remotely).
// Base path: /api/v1. Both backends implement the same contract (ADR-0006).

// ---- auth ------------------------------------------------------------------

export const LoginRequest = z.object({
  email: z.email().max(254),
  password: z.string().min(1).max(256),
  totp: z
    .string()
    .regex(/^\d{6}$/)
    .optional(),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

export const User = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: Role,
  totpEnabled: z.boolean(),
});
export type User = z.infer<typeof User>;

export const TokenResponse = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  /** access token lifetime in seconds */
  expiresIn: z.number().int().positive(),
  user: User,
});
export type TokenResponse = z.infer<typeof TokenResponse>;

export const RefreshRequest = z.object({ refreshToken: z.string().min(16).max(512) });
export type RefreshRequest = z.infer<typeof RefreshRequest>;

export const ApiErrorCode = z.enum([
  'bad_request',
  'unauthorized',
  'invalid_credentials',
  'totp_required',
  'totp_setup_required',
  'forbidden',
  'not_found',
  'conflict',
  'rate_limited',
  'evcc_unavailable',
  'evcc_error',
  /** a service of STE Connect is not set up (e.g. e-mail delivery) */
  'unavailable',
  'internal',
]);
export type ApiErrorCode = z.infer<typeof ApiErrorCode>;

export const ApiError = z.object({
  error: ApiErrorCode,
  message: z.string(),
});
export type ApiError = z.infer<typeof ApiError>;

// ---- site and live data ------------------------------------------------------

export const LoadpointKind = z.enum(['wallbox', 'heatpump']);
export type LoadpointKind = z.infer<typeof LoadpointKind>;

export const VehicleInfo = z.object({
  name: z.string(),
  title: z.string(),
});
export type VehicleInfo = z.infer<typeof VehicleInfo>;

export const LoadpointInfo = z.object({
  /** 1-based, as in the evcc REST API */
  id: z.number().int().positive(),
  title: z.string(),
  kind: LoadpointKind,
});
export type LoadpointInfo = z.infer<typeof LoadpointInfo>;

export const SiteInfo = z.object({
  id: z.string(),
  title: z.string(),
  currency: z.string(),
  loadpoints: z.array(LoadpointInfo),
  vehicles: z.array(VehicleInfo),
  hasBattery: z.boolean(),
  batteryControllable: z.boolean(),
  hasHems: z.boolean(),
  evccVersion: z.string().optional(),
});
export type SiteInfo = z.infer<typeof SiteInfo>;

export const LoadpointLive = z.object({
  id: z.number().int().positive(),
  title: z.string(),
  kind: LoadpointKind,
  /** STE mode; heat pumps only use off/pv/now */
  mode: ChargeMode.optional(),
  power: z.number(),
  enabled: z.boolean(),
  charging: z.boolean(),
  connected: z.boolean(),
  /** §14a dimming active on this loadpoint */
  dimmed: z.boolean(),
  /** session energy in kWh */
  sessionEnergy: z.number().optional(),
  limitSoc: z.number().optional(),
  vehicle: z
    .object({
      name: z.string(),
      title: z.string(),
      soc: z.number().optional(),
      range: z.number().optional(),
    })
    .optional(),
  /** heat pumps: storage temperature °C and target/min temperature */
  temperature: z.number().optional(),
  limitTemperature: z.number().optional(),
  minTemperature: z.number().optional(),
  plan: z
    .object({
      time: z.string(),
      soc: z.number().optional(),
      energy: z.number().optional(),
      active: z.boolean(),
    })
    .optional(),
});
export type LoadpointLive = z.infer<typeof LoadpointLive>;

export const SiteLive = z.object({
  timestamp: z.string(),
  /** false while the gateway has no live connection to evcc */
  online: z.boolean(),
  pv: z.object({ power: z.number() }),
  home: z.object({ power: z.number() }),
  /** > 0 import, < 0 export */
  grid: z.object({ power: z.number() }),
  battery: z
    .object({
      /** > 0 discharging, < 0 charging */
      power: z.number(),
      soc: z.number(),
      capacity: z.number().optional(),
    })
    .optional(),
  loadpoints: z.array(LoadpointLive),
  hems: z.object({
    dimmed: z.boolean(),
    maxConsumptionPower: z.number().optional(),
    /** allowed feed-in in %, < 100 means curtailed */
    curtailedPercent: z.number().optional(),
  }),
  tariff: z.object({
    grid: z.number().optional(),
    feedIn: z.number().optional(),
  }),
  /** share of own energy in home consumption, 0..1 */
  greenShareHome: z.number().optional(),
  batterySettings: z
    .object({
      prioritySoc: z.number().optional(),
      bufferSoc: z.number().optional(),
      bufferStartSoc: z.number().optional(),
      dischargeControl: z.boolean().optional(),
      gridChargeLimit: z.number().nullable().optional(),
    })
    .optional(),
});
export type SiteLive = z.infer<typeof SiteLive>;

// websocket messages gateway -> client
export const LiveMessage = z.discriminatedUnion('type', [
  z.object({ type: z.literal('live'), data: SiteLive }),
  z.object({ type: z.literal('error'), error: ApiErrorCode, message: z.string() }),
]);
export type LiveMessage = z.infer<typeof LiveMessage>;

// websocket messages client -> gateway
export const LiveAuthMessage = z.object({ type: z.literal('auth'), token: z.string().min(16).max(4096) });

// ---- commands ----------------------------------------------------------------

export const SetModeRequest = z.object({ mode: z.union([ChargeMode, HeatPumpMode]) });
export const SetLimitSocRequest = z.object({ soc: z.number().int().min(20).max(100) });
export const SetMinTemperatureRequest = z.object({ temperature: z.number().int().min(20).max(80) });
export const SelectVehicleRequest = z.object({ vehicle: z.string().regex(/^[a-zA-Z0-9_.:-]+$/) });

export const SetPlanRequest = z
  .object({
    time: z.iso.datetime({ offset: true }),
    soc: z.number().int().min(1).max(100).optional(),
    energy: z.number().positive().max(200).optional(),
  })
  .refine((p) => (p.soc === undefined) !== (p.energy === undefined), {
    message: 'exactly one of soc or energy is required',
  });

export const BatterySettingsRequest = z
  .object({
    prioritySoc: z.number().min(0).max(100),
    bufferSoc: z.number().min(0).max(100),
    bufferStartSoc: z.number().min(0).max(100),
    dischargeControl: z.boolean(),
    /** currency/kWh, null removes the limit */
    gridChargeLimit: z.number().min(-1).max(2).nullable(),
  })
  .partial()
  .refine((s) => Object.keys(s).length > 0, { message: 'no setting given' });
export type BatterySettingsRequest = z.infer<typeof BatterySettingsRequest>;

// ---- history -----------------------------------------------------------------

export const HistoryPeriod = z.enum(['day', 'week', 'month', 'year']);
export type HistoryPeriod = z.infer<typeof HistoryPeriod>;

export const EnergyHistoryQuery = z.object({
  period: HistoryPeriod,
  /** local date YYYY-MM-DD inside the period */
  date: z.iso.date(),
});

export const EnergyBucket = z.object({
  start: z.string(),
  /** all values in kWh */
  pv: z.number(),
  home: z.number(),
  gridImport: z.number(),
  gridExport: z.number(),
  batteryCharge: z.number(),
  batteryDischarge: z.number(),
  wallbox: z.number(),
  heatpump: z.number(),
  /** grid import cost in currency, if a tariff is known */
  cost: z.number().optional(),
  /** feed-in revenue in currency */
  revenue: z.number().optional(),
});
export type EnergyBucket = z.infer<typeof EnergyBucket>;

export const EnergyHistory = z.object({
  period: HistoryPeriod,
  from: z.string(),
  to: z.string(),
  buckets: z.array(EnergyBucket),
  totals: EnergyBucket.omit({ start: true }),
  /** share of consumption covered by own generation, 0..1 */
  autarky: z.number().optional(),
  /** share of generation consumed on site, 0..1 */
  selfConsumption: z.number().optional(),
});
export type EnergyHistory = z.infer<typeof EnergyHistory>;

export const PowerSignal = z.string().regex(/^[a-z0-9_]+$/);

export const PowerHistoryQuery = z.object({
  from: z.iso.datetime({ offset: true }),
  to: z.iso.datetime({ offset: true }),
  signals: z.string().optional(),
});

export const PowerSeries = z.object({
  signal: z.string(),
  /** [unix seconds of minute start, avg, min, max] */
  points: z.array(z.tuple([z.number(), z.number(), z.number(), z.number()])),
});
export const PowerHistory = z.object({ from: z.string(), to: z.string(), series: z.array(PowerSeries) });
export type PowerHistory = z.infer<typeof PowerHistory>;

// ---- installer (Phase 4) -----------------------------------------------------

export const TemplateClass = z.enum(['meter', 'charger', 'vehicle', 'hems', 'tariff']);
export type TemplateClass = z.infer<typeof TemplateClass>;

export const TemplateParam = z.object({
  name: z.string(),
  description: z.string(),
  help: z.string().optional(),
  type: z.enum(['String', 'Int', 'Float', 'Bool', 'Duration', 'Choice', 'List']),
  required: z.boolean(),
  advanced: z.boolean(),
  /** secrets: never shown again after saving */
  secret: z.boolean(),
  default: z.union([z.string(), z.number(), z.boolean()]).optional(),
  example: z.string().optional(),
  choice: z.array(z.string()).optional(),
  unit: z.string().optional(),
  /** param applies only to these meter usages */
  usages: z.array(z.string()).optional(),
  /** modbus presets: defaults for port and device id */
  modbus: z.object({ port: z.number().optional(), id: z.number().optional() }).optional(),
});
export type TemplateParam = z.infer<typeof TemplateParam>;

export const TemplateInfo = z.object({
  template: z.string(),
  group: z.string().optional(),
  products: z.array(z.object({ brand: z.string(), description: z.string() })),
  sponsorship: z.boolean(),
  requirements: z.string().optional(),
  caveats: z.array(z.string()).optional(),
  params: z.array(TemplateParam),
});
export type TemplateInfo = z.infer<typeof TemplateInfo>;

export const DeviceTestResult = z.object({
  ok: z.boolean(),
  values: z.record(z.string(), z.object({ value: z.unknown(), error: z.string() })).optional(),
  error: z.string().optional(),
});
export type DeviceTestResult = z.infer<typeof DeviceTestResult>;

export const ValidationIssue = z.object({
  path: z.string(),
  message: z.string(),
  severity: z.enum(['error', 'warning']),
});
export type ValidationIssue = z.infer<typeof ValidationIssue>;

export const RevisionStatus = z.enum(['active', 'superseded', 'failed', 'imported']);

export const ConfigRevision = z.object({
  id: z.number(),
  createdAt: z.string(),
  author: z.string(),
  comment: z.string(),
  status: RevisionStatus,
  error: z.string().nullable(),
});
export type ConfigRevision = z.infer<typeof ConfigRevision>;

export const ApplyResult = z.object({
  ok: z.boolean(),
  revision: ConfigRevision,
  /** set when the new configuration failed and the previous one was restored */
  rolledBack: z.boolean(),
  error: z.string().optional(),
  issues: z.array(ValidationIssue).optional(),
});
export type ApplyResult = z.infer<typeof ApplyResult>;

export const Diagnostics = z.object({
  evcc: z.object({
    online: z.boolean(),
    version: z.string().optional(),
    startupCompleted: z.boolean(),
    fatal: z.array(
      z.object({ class: z.string().optional(), device: z.string().optional(), error: z.string() }),
    ),
    configDirty: z.boolean().optional(),
    adminAvailable: z.boolean(),
  }),
  devices: z.array(
    z.object({
      class: z.string(),
      name: z.string(),
      ok: z.boolean().optional(),
      detail: z.unknown().optional(),
    }),
  ),
  log: z.array(z.string()),
  /** this Pi: device id and serial assigned by STE Connect (ADR-0016) */
  gateway: z.object({ deviceId: z.string().optional(), serial: z.string().optional() }).optional(),
});
export type Diagnostics = z.infer<typeof Diagnostics>;

/** evcc sponsorship (ADR-0007), from the evcc state key `sponsor`; the token itself never leaves evcc */
export const SponsorStatus = z.object({
  configured: z.boolean(),
  /** subject of the token as reported by evcc */
  name: z.string().optional(),
  /** undefined: no expiry */
  expiresAt: z.string().optional(),
  /** evcc: less than 30 days left */
  expiresSoon: z.boolean(),
  /** redacted by evcc, e.g. "eyJhbG.......Xk9Q" */
  token: z.string().optional(),
  /** where evcc got the token from: STE (evcc settings) or an evcc.yaml entry */
  source: z.enum(['ste', 'yaml']).optional(),
  /** sponsorship error reported by evcc at startup (e.g. expired token) */
  error: z.string().optional(),
});
export type SponsorStatus = z.infer<typeof SponsorStatus>;

export const SetSponsorTokenRequest = z.object({ token: z.string().trim().min(20).max(4096) });

export const SponsorResult = z.object({
  ok: z.boolean(),
  status: SponsorStatus,
  error: z.string().optional(),
});
export type SponsorResult = z.infer<typeof SponsorResult>;

export const ChecklistItem = z.object({
  id: z.string(),
  label: z.string(),
  done: z.boolean(),
  note: z.string().max(500).optional(),
});
/** drawn on screen; SVG path data in a 600×200 box (compact, no images) */
export const SignatureInput = z.object({
  role: z.enum(['installer', 'customer']),
  name: z.string().trim().min(2).max(120),
  path: z
    .string()
    .min(10)
    .max(20_000)
    .regex(/^[ML0-9 .-]+$/, 'ungültige Unterschrift'),
});
export type SignatureInput = z.infer<typeof SignatureInput>;

export const Signature = z.object({ name: z.string(), path: z.string(), signedAt: z.string() });
export type Signature = z.infer<typeof Signature>;

/** captured when the protocol is completed, so a later printout shows the state at handover */
export const ProtocolSnapshot = z.object({
  siteTitle: z.string(),
  evccVersion: z.string().optional(),
  gatewayVersion: z.string().optional(),
  devices: z.array(z.object({ kind: z.string(), name: z.string() })),
  measurements: z.string().optional(),
  /** master data at handover (ADR-0017) */
  profile: SiteProfile.optional(),
  /** fuse of the house connection in A, if configured */
  houseConnectionA: z.number().optional(),
  /** §14a control: device template or "raw", absent without control */
  control14a: z.string().optional(),
});
export type ProtocolSnapshot = z.infer<typeof ProtocolSnapshot>;

export const Checklist = z.object({
  items: z.array(ChecklistItem),
  customer: z.string().max(200).optional(),
  installer: z.string().max(200).optional(),
  updatedAt: z.string().optional(),
  // read-only for clients, maintained by the gateway
  signatures: z.object({ installer: Signature.optional(), customer: Signature.optional() }).optional(),
  completedAt: z.string().optional(),
  snapshot: ProtocolSnapshot.optional(),
  /** the installer released the completed protocol to the customer */
  sharedWithCustomer: z.boolean().optional(),
});
export type Checklist = z.infer<typeof Checklist>;

// ---- audit -------------------------------------------------------------------

export const AuditEntry = z.object({
  id: z.number(),
  timestamp: z.string(),
  userId: z.string(),
  userEmail: z.string(),
  role: Role,
  source: z.enum(['local', 'cloud', 'system']),
  action: z.string(),
  target: z.string().nullable(),
  params: z.unknown(),
  result: z.enum(['ok', 'error']),
  error: z.string().nullable(),
});
export type AuditEntry = z.infer<typeof AuditEntry>;

// ---- emergency login on the Pi with the recovery password (ADR-0016) ------------------------

export const RecoveryLoginRequest = z.object({ password: z.string().trim().min(1).max(64) });
export type RecoveryLoginRequest = z.infer<typeof RecoveryLoginRequest>;

/** how long the emergency login lasts; there is no refresh */
export const RECOVERY_SESSION_SECONDS = 60 * 60;
